'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Modal } from '../Modal';
import { Form } from '../../molecules/Form';
import { FormField } from '../../molecules/FormField';
import { Input } from '../../atoms/Input';
import { Select } from '../../atoms/Select';
import { Button } from '../../atoms/Button';
import { Flex } from '../../atoms/Flex';
import { Notice } from '../../atoms/Notice';
import './ContactFormModal.css';

// =============================================================================
// Types
// =============================================================================

export interface Country {
  id: string;
  name: string;
}

export interface ContactFormData {
  title: string;
  fname: string;
  lname: string;
  address1: string;
  address2: string;
  postcode: string;
  town: string;
  county: string;
  country: string;
  telephone: string;
  mobile: string;
  email: string;
  orgtype: string;
  company: string;
  companynumber: string;
}

export interface ContactFormSubmitResult {
  success: boolean;
  contactId?: string;
  errors?: Record<string, string>;
}

export interface ContactFormModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when the modal should close */
  onClose: () => void;
  /** Called after a contact is successfully created */
  onSuccess: (contactId: string) => void;
  /** Async submit handler — receives validated form data */
  onSubmit: (data: ContactFormData) => Promise<ContactFormSubmitResult>;
  /** Country list for the country dropdown */
  countries: Country[];
  /** Pre-fill form for editing — when provided, defaults to "Edit Contact" / "Update Contact" */
  initialValues?: Partial<ContactFormData>;
  /** Modal title (defaults based on whether initialValues is provided) */
  title?: string;
  /** Submit button label (defaults based on whether initialValues is provided) */
  submitLabel?: string;
  /** Ideal Postcodes API key — enables address search when provided and script is loaded */
  addressFinderApiKey?: string;
  /** Additional CSS class */
  className?: string;
}

// =============================================================================
// Validation
// =============================================================================

const VALIDATIONS: Record<string, { pattern: RegExp; message: string }> = {
  fname: {
    pattern: /^[A-Z\xC0-\xDDa-z\xE0-\xFF][A-Z\xC0-\xDDa-z\xE0-\xFF'\-.\s]*$/,
    message: 'Please enter a valid first name',
  },
  lname: {
    pattern: /^[A-Z\xC0-\xDDa-z\xE0-\xFF][A-Z\xC0-\xDDa-z\xE0-\xFF'\-.\s]*$/,
    message: 'Please enter a valid last name',
  },
  address1: {
    pattern: /^[A-Z\xC0-\xDDa-z\xE0-\xFF0-9][A-Z\xC0-\xDDa-z\xE0-\xFF0-9'\-./,\s]+$/,
    message: 'Please enter a valid address',
  },
  address2: {
    pattern: /^$|^[A-Z\xC0-\xDDa-z\xE0-\xFF0-9][A-Z\xC0-\xDDa-z\xE0-\xFF0-9'\-./,\s]+$/,
    message: 'Please enter a valid address',
  },
  postcode: {
    pattern: /^[A-Z0-9\-\s]{2,10}$/i,
    message: 'Please enter a valid postcode',
  },
  town: {
    pattern: /^[A-Z\xC0-\xDDa-z\xE0-\xFF][A-Z\xC0-\xDDa-z\xE0-\xFF0-9\-&.'\s]*$/,
    message: 'Please enter a valid town',
  },
  county: {
    pattern: /^$|^[A-Z\xC0-\xDDa-z\xE0-\xFF][A-Z\xC0-\xDDa-z\xE0-\xFF0-9\-&.'\s]*$/,
    message: 'Please enter a valid county',
  },
  telephone: {
    pattern: /^(?=(?:\D*\d){7,})(?:\+\d{1,3}[-.()\s]?)?[0-9().\s-]+$/,
    message: 'Please enter a valid telephone number (e.g. +44.1234567890)',
  },
  mobile: {
    pattern: /^$|^(?=(?:\D*\d){7,})(?:\+\d{1,3}[-.()\s]?)?[0-9().\s-]+$/,
    message: 'Please enter a valid mobile number (e.g. +44.1234567890)',
  },
  email: {
    pattern: /^[a-z0-9\-_.+]+@[a-z0-9\-_.]+\.[a-z]{2,}$/i,
    message: 'Please enter a valid email address',
  },
  company: {
    pattern: /^[A-Z\xC0-\xDDa-z\xE0-\xFF0-9!@'&*\-_:.,\s]+$/,
    message: 'Please enter a valid company name',
  },
  companynumber: {
    pattern: /^(?:[A-Z]{2})?[A-Z0-9]{5,13}$/i,
    message: 'Please enter a valid company number',
  },
};

const ALWAYS_REQUIRED = new Set([
  'fname',
  'lname',
  'address1',
  'postcode',
  'town',
  'telephone',
  'email',
]);

const ORGTYPE_OPTIONS = [
  { value: '0', label: 'Individual' },
  { value: '1', label: 'Company' },
  { value: '1:STRA', label: 'UK Sole Trader' },
  { value: '1:LTD', label: 'UK Limited Company' },
  { value: '1:PLC', label: 'UK Public Limited Company' },
  { value: '1:LLP', label: 'UK Limited Liability Partnership' },
  { value: '2', label: 'Nonprofit organisation' },
];

function isFieldRequired(name: string, orgtype: string): boolean {
  if (ALWAYS_REQUIRED.has(name)) return true;
  if (name === 'company') return /^[12]/.test(orgtype);
  if (name === 'companynumber') return ['1:LTD', '1:PLC', '1:LLP'].includes(orgtype);
  return false;
}

function validateField(name: string, value: string, orgtype: string): string | null {
  if (isFieldRequired(name, orgtype) && !value.trim()) {
    return 'This field is required';
  }
  const validation = VALIDATIONS[name];
  if (validation && value.trim() && !validation.pattern.test(value)) {
    return validation.message;
  }
  return null;
}

// =============================================================================
// Component
// =============================================================================

const FORM_ID = 'hi-ui-contact-form-modal';

export const ContactFormModal: React.FC<ContactFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onSubmit,
  countries,
  initialValues,
  addressFinderApiKey,
  title,
  submitLabel,
  className = '',
}) => {
  const isEdit = !!initialValues;
  const modalTitle = title ?? (isEdit ? 'Edit Contact' : 'Add Contact');
  const modalSubmitLabel = submitLabel ?? (isEdit ? 'Update Contact' : 'Create Contact');
  const [orgtype, setOrgtype] = useState(initialValues?.orgtype ?? '0');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const addressFinderRef = useRef<any>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const handleSubmitClick = useCallback(() => {
    const form = document.getElementById(FORM_ID) as HTMLFormElement | null;
    form?.requestSubmit();
  }, []);

  // Reset state when modal opens
  /* eslint-disable react-hooks/set-state-in-effect -- intentional: reset all form state when the modal transitions from closed to open */
  useEffect(() => {
    if (isOpen) {
      setOrgtype(initialValues?.orgtype ?? '0');
      setFieldErrors({});
      setServerErrors({});
      setSubmitting(false);
    }
  }, [isOpen, initialValues]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Initialise Ideal Postcodes address finder when modal opens
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const IdealPostcodes = (window as any).IdealPostcodes;
    if (!isOpen || !addressFinderApiKey || !IdealPostcodes?.AddressFinder?.watch) return;

    // Small delay to ensure DOM is ready inside the modal
    const timer = setTimeout(() => {
      addressFinderRef.current = IdealPostcodes.AddressFinder.watch({
        apiKey: addressFinderApiKey,
        inputField: `#${FORM_ID}-address-search`,
        outputFields: {
          line_1: `#${FORM_ID}-address1`,
          line_2: `#${FORM_ID}-address2`,
          county: `#${FORM_ID}-county`,
          post_town: `#${FORM_ID}-town`,
          postcode: `#${FORM_ID}-postcode`,
        },
        restrictCountries: [
          'GBR',
          'AUS',
          'AUT',
          'BEL',
          'BGR',
          'CAN',
          'CYP',
          'CZE',
          'DNK',
          'EST',
          'FIN',
          'FRA',
          'DEU',
          'GRC',
          'HUN',
          'IRL',
          'ITA',
          'LVA',
          'LTU',
          'LUX',
          'MLT',
          'MCO',
          'NLD',
          'NZL',
          'NOR',
          'POL',
          'PRT',
          'ROU',
          'SVK',
          'SVN',
          'ESP',
          'SWE',
          'CHE',
          'USA',
        ],
        detectCountry: false,
        onLoaded() {
          const dropdown = document.querySelector<HTMLSelectElement>(
            `#${FORM_ID} [name="country"]`
          );
          // eslint-disable-next-line @typescript-eslint/no-this-alias
          const finder = this;
          dropdown?.addEventListener('change', function onCountryChange() {
            const iso2 = dropdown.value;
            const contexts = Object.values(finder.options.contexts) as Array<{ iso_2: string }>;
            for (const context of contexts) {
              if (context.iso_2 === iso2) {
                finder.applyContext(context);
                break;
              }
            }
          });
        },
        hideToolbar: true,
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      if (addressFinderRef.current?.destroy) {
        addressFinderRef.current.destroy();
        addressFinderRef.current = null;
      }
    };
  }, [isOpen, addressFinderApiKey]);

  const showCompany = /^[12]/.test(orgtype);
  const showCompanyNumber = ['1:LTD', '1:PLC', '1:LLP'].includes(orgtype);

  // Merge client + server errors for display
  const allErrors: Record<string, string> = { ...serverErrors, ...fieldErrors };

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      if (!name) return;
      const error = validateField(name, value, orgtype);
      setFieldErrors((prev) => {
        const next = { ...prev };
        if (error) next[name] = error;
        else delete next[name];
        return next;
      });
    },
    [orgtype]
  );

  const handleInput = useCallback(
    (e: React.FormEvent<HTMLInputElement | HTMLSelectElement>) => {
      const target = e.target as HTMLInputElement;
      const { name } = target;
      if (name && (fieldErrors[name] || serverErrors[name])) {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
        setServerErrors((prev) => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
      }
    },
    [fieldErrors, serverErrors]
  );

  const handleOrgtypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setOrgtype(e.target.value);
    // Clear company errors when hiding fields
    const newOrgtype = e.target.value;
    if (!/^[12]/.test(newOrgtype)) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.company;
        delete next.companynumber;
        return next;
      });
    }
  }, []);

  const handleFormSubmit = useCallback(
    async (formData: FormData) => {
      const data: ContactFormData = {
        /* v8 ignore next */
        title: (formData.get('title') as string) || 'Mr',
        fname: (formData.get('fname') as string) || '',
        lname: (formData.get('lname') as string) || '',
        address1: (formData.get('address1') as string) || '',
        address2: (formData.get('address2') as string) || '',
        postcode: (formData.get('postcode') as string) || '',
        town: (formData.get('town') as string) || '',
        county: (formData.get('county') as string) || '',
        /* v8 ignore next */
        country: (formData.get('country') as string) || 'GB',
        telephone: (formData.get('telephone') as string) || '',
        mobile: (formData.get('mobile') as string) || '',
        email: (formData.get('email') as string) || '',
        /* v8 ignore next */
        orgtype: (formData.get('orgtype') as string) || '0',
        company: (formData.get('company') as string) || '',
        companynumber: (formData.get('companynumber') as string) || '',
      };

      // Client-side validation
      const errors: Record<string, string> = {};
      for (const [key, value] of Object.entries(data)) {
        if (key === 'title' || key === 'country' || key === 'orgtype') continue;
        if (key === 'company' && !showCompany) continue;
        if (key === 'companynumber' && !showCompanyNumber) continue;
        const error = validateField(key, value, data.orgtype);
        if (error) errors[key] = error;
      }

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setServerErrors({});
        const firstErrorField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLInputElement>(
          `#${FORM_ID} [name="${firstErrorField}"]`
        );
        el?.focus();
        return;
      }

      setSubmitting(true);
      setServerErrors({});
      setFieldErrors({});

      try {
        const result = await onSubmit(data);
        if (result.success && result.contactId) {
          setSubmitting(false);
          onSuccess(result.contactId);
          onClose();
        } else {
          setServerErrors(result.errors || { _general: 'An error occurred' });
          setSubmitting(false);
        }
      } catch {
        setServerErrors({
          _general: 'An unexpected error occurred. Please try again.',
        });
        setSubmitting(false);
      }
    },
    [onSubmit, onSuccess, onClose, showCompany, showCompanyNumber]
  );

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmitClick} loading={submitting}>
        {modalSubmitLabel}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="medium"
      footer={footer}
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      className={`hi-ui-contact-form-modal ${className}`.trim()}
    >
      <Form
        key={isOpen ? 'open' : 'closed'}
        id={FORM_ID}
        onSubmit={handleFormSubmit}
        submitting={submitting}
        noValidate
      >
        <Flex direction="column">
          {allErrors._general && <Notice variant="error">{allErrors._general}</Notice>}
          {/* Title */}
          <FormField label="Title" required>
            <Select name="title" defaultValue={initialValues?.title ?? 'Mr'} fullWidth>
              <option value="Mr">Mr</option>
              <option value="Mrs">Mrs</option>
              <option value="Miss">Miss</option>
              <option value="Ms">Ms</option>
              <option value="Dr">Dr</option>
            </Select>
          </FormField>

          {/* First Name + Last Name */}
          <Flex gap="md" className="hi-ui-contact-form-modal__pair-row">
            <FormField label="First Name" required error={allErrors.fname} fullWidth>
              <Input
                name="fname"
                defaultValue={initialValues?.fname}
                onBlur={handleBlur}
                onInput={handleInput}
                fullWidth
              />
            </FormField>
            <FormField label="Last Name" required error={allErrors.lname} fullWidth>
              <Input
                name="lname"
                defaultValue={initialValues?.lname}
                onBlur={handleBlur}
                onInput={handleInput}
                fullWidth
              />
            </FormField>
          </Flex>

          {/* Address Search (only when Ideal Postcodes is available) */}
          {addressFinderApiKey && (
            <FormField label="Address Search" fullWidth>
              <Input
                id={`${FORM_ID}-address-search`}
                placeholder="Start typing address here"
                fullWidth
              />
            </FormField>
          )}

          {/* Address Line 1 */}
          <FormField label="Address Line 1" required error={allErrors.address1} fullWidth>
            <Input
              id={`${FORM_ID}-address1`}
              name="address1"
              defaultValue={initialValues?.address1}
              onBlur={handleBlur}
              onInput={handleInput}
              fullWidth
            />
          </FormField>

          {/* Address Line 2 */}
          <FormField label="Address Line 2" error={allErrors.address2} fullWidth>
            <Input
              id={`${FORM_ID}-address2`}
              name="address2"
              defaultValue={initialValues?.address2}
              onBlur={handleBlur}
              onInput={handleInput}
              fullWidth
            />
          </FormField>

          {/* Town + County */}
          <Flex gap="md" className="hi-ui-contact-form-modal__pair-row">
            <FormField label="Town" required error={allErrors.town} fullWidth>
              <Input
                id={`${FORM_ID}-town`}
                name="town"
                defaultValue={initialValues?.town}
                onBlur={handleBlur}
                onInput={handleInput}
                fullWidth
              />
            </FormField>
            <FormField label="County" error={allErrors.county} fullWidth>
              <Input
                id={`${FORM_ID}-county`}
                name="county"
                defaultValue={initialValues?.county}
                onBlur={handleBlur}
                onInput={handleInput}
                fullWidth
              />
            </FormField>
          </Flex>

          {/* Postcode + Country */}
          <Flex gap="md" className="hi-ui-contact-form-modal__pair-row">
            <FormField label="Postcode" required error={allErrors.postcode} fullWidth>
              <Input
                id={`${FORM_ID}-postcode`}
                name="postcode"
                defaultValue={initialValues?.postcode}
                onBlur={handleBlur}
                onInput={handleInput}
                fullWidth
              />
            </FormField>
            <FormField label="Country" required fullWidth>
              <Select name="country" defaultValue={initialValues?.country ?? 'GB'} fullWidth>
                {countries.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </FormField>
          </Flex>

          {/* Telephone + Mobile */}
          <Flex gap="md" className="hi-ui-contact-form-modal__pair-row">
            <FormField label="Telephone" required error={allErrors.telephone} fullWidth>
              <Input
                name="telephone"
                defaultValue={initialValues?.telephone}
                placeholder="+44.1234567890"
                onBlur={handleBlur}
                onInput={handleInput}
                fullWidth
              />
            </FormField>
            <FormField label="Mobile" error={allErrors.mobile} fullWidth>
              <Input
                name="mobile"
                defaultValue={initialValues?.mobile}
                placeholder="+44.1234567890"
                onBlur={handleBlur}
                onInput={handleInput}
                fullWidth
              />
            </FormField>
          </Flex>

          {/* Email */}
          <FormField label="Email" required error={allErrors.email} fullWidth>
            <Input
              name="email"
              type="email"
              defaultValue={initialValues?.email}
              onBlur={handleBlur}
              onInput={handleInput}
              fullWidth
            />
          </FormField>

          {/* Organisation Type */}
          <FormField label="Organisation Type">
            <Select name="orgtype" value={orgtype} onChange={handleOrgtypeChange} fullWidth>
              {ORGTYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormField>

          {/* Company Name (conditional) */}
          {showCompany && (
            <FormField
              label="Company Name"
              required={isFieldRequired('company', orgtype)}
              error={allErrors.company}
              fullWidth
            >
              <Input
                name="company"
                defaultValue={initialValues?.company}
                onBlur={handleBlur}
                onInput={handleInput}
                fullWidth
              />
            </FormField>
          )}

          {/* Company Number (conditional) */}
          {showCompanyNumber && (
            <FormField label="Company Number" required error={allErrors.companynumber} fullWidth>
              <Input
                name="companynumber"
                defaultValue={initialValues?.companynumber}
                onBlur={handleBlur}
                onInput={handleInput}
                fullWidth
              />
            </FormField>
          )}
        </Flex>
      </Form>
    </Modal>
  );
};

ContactFormModal.displayName = 'ContactFormModal';
